import React, { ReactElement, useState, useCallback } from 'react';
import ReactDOM from 'react-dom';
import {
  Row,
  Col,
  Button,
  Spinner,
  Modal,
} from 'react-bootstrap';
import { getIn, Formik } from 'formik';
import Swal from 'sweetalert2';
import {
  Breed, BreedGrouping, BreedCategory, BreedColor, BreedSelect,
} from '../../../structures/models/breed';
import TextField from '../Partials/TextField';
import {
  BREED_SIZE_VALUES,
  BREED_PEDIGREE_TYPES,
  COAT_TYPES,
  COAT_LENGTHS,
  CLOUDINARY_UPLOAD_PRESET_BREED,
  ENERGY_LEVELS,
  SHEDDING_LEVELS,
} from '../../../utils/constants';
import CheckboxField from '../Partials/CheckboxField';
import TinyMceField from '../Partials/TinyMceField';
import CreatableSelectField from '../Partials/CreatableSelectField';
import ReactSelectField from '../Partials/ReactSelectField';
import {
  CreateBreedRequest,
  UpdateBreedRequest,
} from '../../../structures/requests/breed';
import { createBreed, updateBreed, uploadBreedMedia } from '../../../utils/ajax/breedAjax';
import CloudinaryUploadField from '../Partials/Cloudinary/CloudinaryUploadField';
import { isLaravelError } from '../../../structures/responses/axios';
import { BreedValidation } from './validation';
import DeleteBreedButton from './DeleteBreedButton';

type BreedFormValues = {
  currentBreed: Breed | null
  breedColors: BreedColor[],
  allBreeds: Array<Breed>
  breedGroupings: Array<BreedGrouping>,
  breedCategories: Array<BreedCategory>,
  canDelete: number
  // tinyInt converted out of a boolean telling us if
  // a deletion is possible or not based on a backend role check
};

const generateBreedSelectArray = (breeds: Array<Breed>): Array<object> => {
  return breeds.map((breed) => {
    return {
      label: breed.name,
      value: breed.id,
    };
  });
};

const generateGroupingSelectArray = (breedGroupings: Array<BreedGrouping>): Array<object> => {
  return breedGroupings.map((breedGrouping) => {
    return {
      label: breedGrouping.name,
      value: breedGrouping.id,
    };
  });
};

const generateCategoriesSelectArray = (breedCategories: Array<BreedCategory>): Array<object> => {
  return breedCategories.map((breedCategory) => {
    return {
      label: breedCategory.name,
      value: breedCategory.id,
    };
  });
};

const generateColorsSelectArray = (breedColors: BreedColor[]): BreedSelect[] => {
  return breedColors.map((breedColor) => {
    return {
      label: breedColor.name,
      value: breedColor.id,
    };
  });
};

const BreedForm: React.FC<BreedFormValues> = (props): ReactElement => {
  const {
    currentBreed: importedBreed,
    breedColors,
    allBreeds,
    breedGroupings,
    breedCategories,
    canDelete,
  } = props;
  const [currentBreed, setCurrentBreed] = useState<Breed | null>(importedBreed);

  const formProps = {
    initialValues: {
      name: currentBreed?.name || '',
      size: currentBreed?.size || [],
      colors: currentBreed?.colors ? currentBreed.colors.map(({ id }) => id) : [],
      description: currentBreed?.description || '',
      breed_grouping_id: currentBreed?.breed_grouping_id || null,
      breed_categories: currentBreed?.breed_categories || [],
      pedigree: currentBreed?.pedigree || BREED_PEDIGREE_TYPES.Purebred,
      hypoallergenic: !!currentBreed?.hypoallergenic,
      temperament_guide: currentBreed?.temperament_guide || '',
      shedding: currentBreed?.shedding || null,
      energy_level: currentBreed?.energy_level || null,
      energy_level_guide: currentBreed?.energy_level_guide || '',
      lifespan_guide: currentBreed?.lifespan_guide || '',
      grooming_guide: currentBreed?.grooming_guide || '',
      health_guide: currentBreed?.health_guide || '',
      appearance_guide: currentBreed?.appearance_guide || '',
      shedding_guide: currentBreed?.shedding_guide || '',
      size_description: currentBreed?.size_description || '',
      slug: currentBreed?.slug || '',
      alternative_breed_names: currentBreed?.alternative_breed_names || [],
      subtitle: currentBreed?.subtitle || '',
      history: currentBreed?.history || '',
      parent_a_id: currentBreed?.parent_a_id || null,
      parent_b_id: currentBreed?.parent_b_id || null,
      super_id: currentBreed?.super_id || null,
      usable: currentBreed?.usable || true,
      legacy_id: currentBreed?.legacy_id || null,
      lifespan: currentBreed?.lifespan || '',
      coat_description: currentBreed?.coat_description || '',
      coat_length: currentBreed?.coat_length || [],
      coat_type: currentBreed?.coat_type || [],
      guide_title: currentBreed?.guide_title || '',
      guide_subtitle: currentBreed?.guide_subtitle || '',
      is_popular: currentBreed?.is_popular || false,
      is_popular_sort_order: currentBreed?.is_popular_sort_order || [],
      redirect: false,
      faq: currentBreed?.faq || [],
    },
    validateOnChange: true,
  };
  const [currentBreedSelection] = useState(generateBreedSelectArray(allBreeds));
  const [breedGroupingSelection] = useState(generateGroupingSelectArray(breedGroupings));
  const [breedCategoriesSelection] = useState(generateCategoriesSelectArray(breedCategories));
  const [cloudinaryIsUpdating, setCloudinaryIsUpdating] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [loading, setLoading] = useState(false);
  const [shouldUploadMedia, setShouldUploadMedia] = useState(false);
  const [mediaTouched, setMediaTouched] = useState(false);
  const [mediaUploadError, setMediaUploadError] = useState('');
  const [shouldRedirect, setShouldRedirect] = useState(false);
  const [redirectRoute, setRedirectRoute] = useState(currentBreed?.route || '');

  const deleteBreedButton = (canDelete && currentBreed)
    ? (<DeleteBreedButton currentBreed={currentBreed} />)
    : (<></>);

  const hideModal = useCallback(() => {
    if (shouldRedirect && !mediaUploadError) {
      if (redirectRoute) {
        window.location.href = redirectRoute;
      } else {
        Swal.fire(
          'Error',
          'Could not redirect to Breed page.',
          'error',
        )
          .then(() => {
            setMediaTouched(false);
            setShowModal(false);
          });
      }
    } else if (!mediaUploadError) {
      setMediaTouched(false);
      setShowModal(false);
    } else {
      setShowModal(false);
    }
  }, [shouldRedirect, mediaUploadError, redirectRoute]);

  return (
    <>
      <div className="p-4 m-3 bg-white">
        <Formik
          {...formProps}
          onSubmit={(values, helpers) => {
            const { redirect } = values;
            if (loading || cloudinaryIsUpdating) {
              return;
            }
            setLoading(true);
            if (!currentBreed) {
              const createValues: CreateBreedRequest = { ...values };

              createValues.faq = createValues?.faq
                ?.filter((faq) => faq && faq.question && faq.answer) || null;

              createBreed(createValues)
                .then((breed) => {
                  setRedirectRoute(breed?.route || '');
                  Swal.fire(
                    'Success',
                    `${breed?.name || 'Breed'} created successfully!`,
                    'success',
                  )
                    .then(() => {
                      setCurrentBreed(breed);
                      setLoading(false);
                      if (mediaTouched) {
                        setShouldRedirect(redirect);
                        setShouldUploadMedia(true);
                      } else if (redirect) {
                        if (breed?.route) {
                          window.location.href = breed?.route;
                        } else {
                          Swal.fire(
                            'Error',
                            'Could not redirect to Breed page.',
                            'error',
                          )
                            .then(() => {
                              setMediaTouched(false);
                              setShowModal(false);
                            });
                        }
                      } else {
                        setShouldRedirect(false);
                      }
                    });
                })
                .catch((e) => {
                  Swal.fire(
                    'Error',
                    e.response?.data?.message || e.response?.message || 'Something went wrong. Check your connection and try again.',
                    'error',
                  )
                    .then(() => {
                      if (e.response?.status === 422) {
                        // eslint-disable-next-line guard-for-in,no-restricted-syntax
                        for (const index in e.response.data.errors) {
                          helpers.setFieldError(index, e.response.data.errors[index]);
                        }
                      }
                      setLoading(false);
                    });
                });
            } else {
              const updateValues: UpdateBreedRequest = { ...values };
              updateValues.faq = updateValues?.faq
                ?.filter((faq) => faq && faq.question && faq.answer) || null;

              updateBreed(updateValues, currentBreed.id)
                .then((breed) => {
                  setRedirectRoute(breed?.route || '');
                  Swal.fire(
                    'Success',
                    `${breed?.name || 'Breed'} updated successfully!`,
                    'success',
                  )
                    .then(() => {
                      setCurrentBreed(breed);
                      setLoading(false);
                      if (mediaTouched) {
                        setShouldRedirect(redirect);
                        setShouldUploadMedia(true);
                      } else if (redirect) {
                        if (breed?.route) {
                          window.location.href = breed?.route;
                        } else {
                          Swal.fire(
                            'Error',
                            'Could not redirect to Breed page.',
                            'error',
                          )
                            .then(() => {
                              setMediaTouched(false);
                              setShowModal(false);
                            });
                        }
                      } else {
                        setShouldRedirect(false);
                      }
                    });
                })
                .catch((e) => {
                  Swal.fire(
                    'Error',
                    e.response?.data?.message || e.response?.message || 'Something went wrong. Check your connection and try again.',
                    'error',
                  )
                    .then(() => {
                      if (e.response?.status === 422) {
                        // eslint-disable-next-line guard-for-in,no-restricted-syntax
                        for (const index in e.response.data.errors) {
                          helpers.setFieldError(index, e.response.data.errors[index]);
                        }
                      }
                      setLoading(false);
                    });
                });
            }
          }}
          validationSchema={BreedValidation}
        >
          {(formikProps) => {
            const {
              values,
              handleSubmit,
              setFieldValue,
              dirty,
              isValid,
            } = formikProps;
            let parentsHidden = '';
            if (values.pedigree === BREED_PEDIGREE_TYPES.Purebred) {
              parentsHidden = 'hidden';
            } else {
              parentsHidden = '';
            }
            let buttonMesage = 'Save and Continue';

            if ((!dirty && !mediaTouched) && currentBreed) {
              buttonMesage = 'All Changes Saved';
            }
            const disabledSetting = (!dirty && !mediaTouched)
              || cloudinaryIsUpdating
              || loading
              || !isValid
              ? { disabled: true } : {};
            return (
              <>
                <Row>
                  <Col xs={12}>
                    <h2>Titles</h2>
                  </Col>

                  <Col xs={12} md={4}>
                    <TextField
                      formikProps={formikProps}
                      fieldName="name"
                      visibleName="Name"
                    />
                  </Col>

                  <Col xs={12} md={4}>
                    <TextField
                      formikProps={formikProps}
                      fieldName="subtitle"
                      visibleName="Subtitle"
                      formText="the smaller text that appears under the title"
                    />
                  </Col>

                  <Col xs={12} md={4}>
                    <TextField
                      formikProps={formikProps}
                      fieldName="slug"
                      visibleName="Slug"
                    />
                  </Col>

                  <Col xs={12} md={4}>
                    <TextField
                      formikProps={formikProps}
                      fieldName="guide_title"
                      visibleName="Guide Title"
                    />
                  </Col>

                  <Col xs={12} md={4}>
                    <TextField
                      formikProps={formikProps}
                      fieldName="guide_subtitle"
                      visibleName="Guide Subtitle"
                    />
                  </Col>
                </Row>

                <Row>
                  <Col xs={12}>
                    <h2>Description and Guides</h2>
                  </Col>

                  <Col xs={12} md={4}>
                    <TinyMceField
                      formikProps={formikProps}
                      fieldName="description"
                      visibleName="Summary/Description"
                    />
                  </Col>

                  <Col xs={12} md={4}>
                    <TinyMceField
                      formikProps={formikProps}
                      fieldName="temperament_guide"
                      visibleName="Temperament Guide"
                    />
                  </Col>

                  <Col xs={12} md={4}>
                    <TinyMceField
                      formikProps={formikProps}
                      fieldName="health_guide"
                      visibleName="Health Guide"
                    />
                  </Col>

                  <Col xs={12} md={4}>
                    <TinyMceField
                      formikProps={formikProps}
                      fieldName="appearance_guide"
                      visibleName="Appearance"
                    />
                  </Col>

                  <Col xs={12} md={4}>
                    <TinyMceField
                      formikProps={formikProps}
                      fieldName="shedding_guide"
                      visibleName="Shedding Guide"
                    />
                  </Col>
                  <Col xs={12} md={4}>
                    <TinyMceField
                      formikProps={formikProps}
                      fieldName="grooming_guide"
                      visibleName="Grooming Guide"
                    />
                  </Col>
                  <Col xs={12} md={4}>
                    <TinyMceField
                      formikProps={formikProps}
                      fieldName="energy_level_guide"
                      visibleName="Activity Level Guide"
                    />
                  </Col>
                  <Col xs={12} md={4}>
                    <TinyMceField
                      formikProps={formikProps}
                      fieldName="history"
                      visibleName="History"
                    />
                  </Col>
                </Row>

                <Row>
                  <Col xs={12}>
                    <h2>General Information</h2>
                  </Col>

                  <Col xs={12} md={4}>
                    <ReactSelectField
                      isMulti={true}
                      formikProps={formikProps}
                      fieldName="colors"
                      visibleName="Possible Colors"
                      possibleValues={generateColorsSelectArray(breedColors)}
                    />
                  </Col>

                  <Col xs={12} md={4}>
                    <ReactSelectField
                      isMulti={false}
                      formikProps={formikProps}
                      fieldName="breed_grouping_id"
                      visibleName="Group"
                      possibleValues={breedGroupingSelection}
                    />
                  </Col>

                  <Col xs={12} md={4}>
                    <ReactSelectField
                      isMulti={true}
                      formikProps={formikProps}
                      fieldName="breed_categories"
                      visibleName="Categories"
                      possibleValues={breedCategoriesSelection}
                    />
                  </Col>

                  <Col xs={12} md={4}>
                    <ReactSelectField
                      isMulti={true}
                      formikProps={formikProps}
                      fieldName="size"
                      visibleName="Size (generic)"
                      possibleValues={
                        BREED_SIZE_VALUES
                      }
                    />
                  </Col>

                  <Col xs={12} md={4}>
                    <TextField
                      formikProps={formikProps}
                      fieldName="size_description"
                      visibleName="Size (details)"
                    />
                  </Col>

                  <Col xs={12} md={4}>
                    <TextField
                      formikProps={formikProps}
                      fieldName="lifespan"
                      visibleName="Lifespan"
                    />
                  </Col>

                  <Col xs={12} md={4}>
                    <ReactSelectField
                      formikProps={formikProps}
                      fieldName="energy_level"
                      visibleName="Energy Level"
                      possibleValues={
                        ENERGY_LEVELS
                      }
                    />
                  </Col>

                  <Col xs={12} md={4}>
                    <CreatableSelectField
                      isMulti={true}
                      formikProps={formikProps}
                      fieldName="alternative_breed_names"
                      visibleName="Common Nicknames"
                      possibleValues={getIn(values, 'alternative_breed_names')}
                    />
                  </Col>

                  <Col xs={12} md={4}>
                    <CheckboxField
                      formikProps={formikProps}
                      fieldName="hypoallergenic"
                      visibleName="Hypoallergenic"
                    />
                  </Col>

                  <Col xs={12} md={4}>
                    <ReactSelectField
                      formikProps={formikProps}
                      fieldName="shedding"
                      visibleName="Shedding Level"
                      possibleValues={
                        SHEDDING_LEVELS
                      }
                    />
                  </Col>

                  <Col xs={12} md={4}>
                    <TextField
                      formikProps={formikProps}
                      fieldName="coat_description"
                      visibleName="Coat Description"
                    />
                  </Col>

                  <Col xs={12} md={4}>
                    <ReactSelectField
                      isMulti={true}
                      formikProps={formikProps}
                      fieldName="coat_length"
                      visibleName="Coat Length"
                      possibleValues={
                        COAT_LENGTHS
                      }
                    />
                  </Col>

                  <Col xs={12} md={4}>
                    <ReactSelectField
                      isMulti={true}
                      formikProps={formikProps}
                      fieldName="coat_type"
                      visibleName="Coat Types"
                      /* @ts-ignore */
                      possibleValues={COAT_TYPES}
                    />
                  </Col>

                  <Col xs={12} md={4}>
                    <ReactSelectField
                      formikProps={formikProps}
                      fieldName="super_id"
                      visibleName="Super Breed"
                      possibleValues={currentBreedSelection}
                      formText="if this breed is a subcategory of another breed, choose one here."
                    />
                  </Col>

                  <Col xs={12} md={4}>
                    <ReactSelectField
                      formikProps={formikProps}
                      fieldName="pedigree"
                      visibleName="Pedigree"
                      withNullOption={false}
                      possibleValues={
                        BREED_PEDIGREE_TYPES
                      }
                      onChangeCallback={(val) => {
                        if (val === BREED_PEDIGREE_TYPES.Purebred) {
                          setFieldValue('parent_a_id', null);
                          setFieldValue('parent_b_id', null);
                        }
                      }}
                    />
                  </Col>

                  <Col xs={12} md={4}>
                    <ReactSelectField
                      extraClassValues={parentsHidden}
                      formikProps={formikProps}
                      fieldName="parent_a_id"
                      visibleName="Parent A"
                      possibleValues={currentBreedSelection}
                    />
                  </Col>

                  <Col xs={12} md={4}>
                    <ReactSelectField
                      extraClassValues={parentsHidden}
                      formikProps={formikProps}
                      fieldName="parent_b_id"
                      visibleName="Parent B"
                      possibleValues={currentBreedSelection}
                    />
                  </Col>

                </Row>
                <Row>
                  <Col xs={12}>
                    <h2>Frequently Answered Questions</h2>
                  </Col>
                  <Col xs={12} md={4}>
                    <TextField
                      formikProps={formikProps}
                      fieldName="faq.0.question"
                      visibleName="Question 1"
                      isPairedInput
                    />
                    <TinyMceField
                      formikProps={formikProps}
                      fieldName="faq.0.answer"
                      isPairedInput
                    />
                  </Col>
                  <Col xs={12} md={4}>
                    <TextField
                      formikProps={formikProps}
                      fieldName="faq.1.question"
                      visibleName="Question 2"
                      isPairedInput
                    />
                    <TinyMceField
                      formikProps={formikProps}
                      fieldName="faq.1.answer"
                      isPairedInput
                    />
                  </Col>
                  <Col xs={12} md={4}>
                    <TextField
                      formikProps={formikProps}
                      fieldName="faq.2.question"
                      visibleName="Question 3"
                      isPairedInput
                    />
                    <TinyMceField
                      formikProps={formikProps}
                      fieldName="faq.2.answer"
                      isPairedInput
                    />
                  </Col>
                  <Col xs={12} md={4}>
                    <TextField
                      formikProps={formikProps}
                      fieldName="faq.3.question"
                      visibleName="Question 4"
                      isPairedInput
                    />
                    <TinyMceField
                      formikProps={formikProps}
                      fieldName="faq.3.answer"
                      isPairedInput
                    />
                  </Col>
                  <Col xs={12} md={4}>
                    <TextField
                      formikProps={formikProps}
                      fieldName="faq.4.question"
                      visibleName="Question 5"
                      isPairedInput
                    />
                    <TinyMceField
                      formikProps={formikProps}
                      fieldName="faq.4.answer"
                      isPairedInput
                    />
                  </Col>
                </Row>
                <Row>
                  <Col xs={12}>
                    <h2 className="text-blue-500">System Information</h2>
                  </Col>
                  <Col xs={12} md={4}>
                    <TextField
                      formikProps={formikProps}
                      fieldName="legacy_id"
                      visibleName="Legacy Id"
                      formText="the id of the breed if it came from an older/legacy system; we do not recommend modifying this field without specific reason"
                    />
                  </Col>
                  <Col xs={12} md={4}>
                    <CheckboxField
                      formikProps={formikProps}
                      fieldName="usable"
                      visibleName="Usable"
                      formText="determine if this breed is allowed to be used by new listings."
                    />
                  </Col>
                  <Col xs={12} md={4}>
                    <CheckboxField
                      formikProps={formikProps}
                      fieldName="is_popular"
                      visibleName="Popular Breed"
                      formText="determine if this breed is popular, shows up in the popular breeds section."
                    />
                  </Col>
                </Row>
                <Row>
                  <Col xs={12}>
                    <h2 className="text-blue-500">Media</h2>
                  </Col>
                  <Col xs="auto">
                    <CloudinaryUploadField
                      cloudinaryWidgetParams={{
                        uploadPreset: CLOUDINARY_UPLOAD_PRESET_BREED,
                      }}
                      shouldUpload={shouldUploadMedia}
                      shouldAllowImages={true}
                      shouldAllowVideos={false}
                      shouldAllowExternalMedia={false}
                      initialImageMedia={currentBreed?.photo_gallery}
                      uploadImageFunction={currentBreed?.id ? async (unsavedMedia) => {
                        try {
                          return await uploadBreedMedia(currentBreed.id, unsavedMedia);
                        } catch (err) {
                          setMediaUploadError('Could not upload media.');
                          return isLaravelError(err)
                            ? err.response.data.message
                            : 'There was an issue uploading that image';
                        }
                      } : undefined}
                      maxMediaCount={5}
                      updateStart={() => {
                        setCloudinaryIsUpdating(true);
                        setShowModal(true);
                        setMediaUploadError('');
                      }}
                      updateComplete={() => {
                        setCloudinaryIsUpdating(false);
                        setShouldUploadMedia(false);
                      }}
                      autoUpdateOnSort={false}
                      autoDeleteOnRemove={false}
                      onSortEnd={() => {
                        if (!mediaTouched) {
                          setMediaTouched(true);
                        }
                      }}
                      onMediaChange={(newMedia) => {
                        if (!mediaTouched) {
                          setMediaTouched(true);
                        }
                      }}
                    />
                  </Col>
                </Row>
                <Row>
                  <Col xs={12}>
                    <Button
                      {...disabledSetting}
                      type="submit"
                      onClick={() => {
                        setFieldValue('redirect', false);
                        handleSubmit();
                      }}
                      className="btn btn-large btn-primary mr-2"
                    >
                      {
                        loading
                          ? (
                            <>
                              <Spinner
                                as="span"
                                animation="border"
                                size="sm"
                                role="status"
                                aria-hidden="true"
                              />
                              <span className="visually-hidden">Loading...</span>
                            </>
                          )
                          : (<>{buttonMesage}</>)
                      }
                    </Button>
                    <Button
                      {...disabledSetting}
                      type="submit"
                      onClick={() => {
                        setFieldValue('redirect', true);
                        handleSubmit();
                      }}
                      className="btn btn-large btn-primary mr-2"
                    >
                      {
                        loading
                          ? (
                            <>
                              <Spinner
                                as="span"
                                animation="border"
                                size="sm"
                                role="status"
                                aria-hidden="true"
                              />
                              <span className="visually-hidden">Loading...</span>
                            </>
                          )
                          : (<>Save and View</>)
                      }
                    </Button>
                    {deleteBreedButton}
                  </Col>
                </Row>
              </>
            );
          }}
        </Formik>
        <Modal
          show={showModal}
          onHide={hideModal}
          backdrop="static"
        >
          <Modal.Body>
            {
              cloudinaryIsUpdating
                ? (
                  <div>
                    <h3>Updating media...</h3>
                    <div className="d-flex justify-content-center align-items-center">
                      <Spinner animation="grow" variant="primary" />
                    </div>
                  </div>
                ) : (
                  <h3>{mediaUploadError || 'Media upload complete'}</h3>
                )
            }
          </Modal.Body>
          {!cloudinaryIsUpdating && (
            <Modal.Footer>
              <Button
                onClick={hideModal}
                variant="primary"
              >
                OK
              </Button>
            </Modal.Footer>
          )}
        </Modal>
      </div>
    </>
  );
};

export default BreedForm;

const elem = document.getElementById('breed-form');

if (elem) {
  const breedAttribute = elem.getAttribute('data-breed');
  const colors = elem.getAttribute('data-colors');
  const allBreedsAttribute = elem.getAttribute('data-all-breeds');
  const breedGroupingsAttribute = elem.getAttribute('data-breed-groupings');
  const breedCategoriesAttribute = elem.getAttribute('data-breed-categories');
  const canDeleteAttribute = elem.getAttribute('data-can-delete');

  const breed = breedAttribute ? JSON.parse(breedAttribute) : null;
  const breedColors = colors ? JSON.parse(colors) : [];
  const allBreeds = allBreedsAttribute ? JSON.parse(allBreedsAttribute) : [];
  const breedGroupings = breedGroupingsAttribute ? JSON.parse(breedGroupingsAttribute) : [];
  const breedCategories = breedCategoriesAttribute ? JSON.parse(breedCategoriesAttribute) : [];
  const canDelete = canDeleteAttribute ? parseInt(canDeleteAttribute, 10) : 0;

  ReactDOM.render(<BreedForm
    currentBreed={breed}
    breedColors={breedColors}
    allBreeds={allBreeds}
    breedGroupings={breedGroupings}
    breedCategories={breedCategories}
    canDelete={canDelete}
  />, document.getElementById('breed-form'));
}
